import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  LinearProgress,
  Chip,
  Paper,
  Button,
  Alert,
  Tooltip,
  useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  getAnalyzeSession, sendAnalyzeMessageStreaming,
  listAnalyzeSessions,
  createAnalyzeSession, createAnalyzeSessionFromTables,
  createAnalyzeSessionFromGoal,
  streamV2RunEvents,
} from "../utils/api";
import AnalyzeEntryComposer from "../components/AnalyzeEntryComposer";
import { useImpersonation } from "../contexts/ImpersonationContext";
import { formatDistanceToNow } from "date-fns";

// ─── Message bubble ───────────────────────────────────────────────────────────

const mdComponents = (theme) => ({
  p: ({ children }) => <p style={{ margin: "0 0 8px", lineHeight: 1.6 }}>{children}</p>,
  h2: ({ children }) => <h2 style={{ fontSize: 15, fontWeight: 700, margin: "12px 0 6px" }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontSize: 14, fontWeight: 700, margin: "10px 0 4px" }}>{children}</h3>,
  ul: ({ children }) => <ul style={{ paddingLeft: 18, margin: "4px 0 8px" }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ paddingLeft: 18, margin: "4px 0 8px" }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 4, lineHeight: 1.6 }}>{children}</li>,
  strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
  hr: () => <hr style={{ border: "none", borderTop: `1px solid ${theme.palette.divider}`, margin: "10px 0" }} />,
  table: ({ children }) => (
    <div style={{ overflowX: "auto", margin: "8px 0" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th style={{ padding: "6px 12px", background: theme.palette.grey[200], fontWeight: 700, textAlign: "left", border: `1px solid ${theme.palette.divider}` }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{ padding: "6px 12px", border: `1px solid ${theme.palette.divider}` }}>{children}</td>
  ),
  code: ({ children }) => (
    <code style={{ background: theme.palette.grey[200], borderRadius: 3, padding: "1px 5px", fontSize: 12, fontFamily: "monospace" }}>{children}</code>
  ),
});

function MessageBubble({ role, content }) {
  const theme = useTheme();
  const isUser = role === "user";
  return (
    <Box sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", mb: 1.5 }}>
      <Box
        sx={{
          maxWidth: "75%",
          px: 2,
          py: 1.5,
          borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          bgcolor: isUser ? theme.palette.primary.main : theme.palette.grey[100],
          color: isUser ? "white" : "text.primary",
          fontSize: 14,
          wordBreak: "break-word",
        }}
      >
        {isUser ? (
          <span style={{ whiteSpace: "pre-wrap" }}>{content}</span>
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents(theme)}>
            {content}
          </ReactMarkdown>
        )}
      </Box>
    </Box>
  );
}

function TypingIndicator({ statusText }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", mb: 1.5, gap: 0.75 }}>
      <Box sx={{ px: 2, py: 1.5, borderRadius: "18px 18px 18px 4px", bgcolor: theme.palette.grey[100], display: "flex", gap: 0.5, alignSelf: "flex-start" }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{
            width: 6, height: 6, borderRadius: "50%", bgcolor: "text.disabled",
            animation: "pulse 1.2s infinite", animationDelay: `${i * 0.2}s`,
            "@keyframes pulse": { "0%,60%,100%": { opacity: 0.3, transform: "scale(1)" }, "30%": { opacity: 1, transform: "scale(1.3)" } },
          }} />
        ))}
      </Box>
      {statusText && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1, fontStyle: "italic" }}>
          {statusText}
        </Typography>
      )}
    </Box>
  );
}

// ─── ML run status card ───────────────────────────────────────────────────────

// Map raw v2 event types into short human-readable progress lines.
// Falls back to the raw event type if we don't have a friendlier rendering.
function _eventLine(ev) {
  if (!ev) return null;
  const p = ev.payload || {};
  switch (ev.type) {
    case "pipeline_started":         return "Starting model build";
    case "readiness_preflight": {
      // Say what the data check found before the build runs, so a known
      // problem isn't first heard about as a mid-run failure.
      const nC = (p.critical || []).length;
      const nW = (p.warnings || []).length;
      if (nC > 0) {
        const first = p.critical[0];
        return `Data check: ${nC} critical issue${nC > 1 ? "s" : ""} — ${first.title}${nC > 1 ? " (and more)" : ""}`;
      }
      if (nW > 0) return `Data check: ${nW} warning${nW > 1 ? "s" : ""}, no blockers`;
      return "Data check: no issues found";
    }
    case "source_copied":            return "Copied source tables into the run dataset";
    case "intent_classified":        return `Identified problem: ${p.problem_type || "?"} · ${p.temporal_structure || ""}`;
    case "columns_classified":       return `Classified ${p.n_columns} columns (${p.n_measures} measures, ${p.n_groups} candidate groups)`;
    case "pred_grid_built":          return `Built prediction grid (${p.row_count} rows)`;
    case "label_built":              return `Built labels (${p.row_count} rows)`;
    case "feature_plan_done":        return `Proposed ${p.n_features} candidate features`;
    case "features_materialized":   return `${p.n_surviving} features materialized in BigQuery`;
    case "features_gated":           return `Kept ${p.n_kept} highest-signal features (dropped ${p.n_dropped})`;
    case "training_done": {
      const m = p.cv_auc != null ? `CV AUC ${p.cv_auc.toFixed(3)}` : (p.mae != null ? `MAE ${p.mae.toFixed(3)}` : "");
      return `Trained ${p.model_kind || "model"}${m ? ` · ${m}` : ""}`;
    }
    case "sanity_warning":           return `Sanity warning: ${p.message}`;
    case "iteration_started":        return `Starting iteration (budget ${p.budget})`;
    case "iteration_turn": {
      const b = p.before_metric != null ? p.before_metric.toFixed(3) : "?";
      const a = p.after_metric != null ? p.after_metric.toFixed(3) : "?";
      return `Experiment ${p.n}: ${p.skill} → ${p.decision} (${b} → ${a})`;
    }
    case "iteration_complete":       return `Iteration done (${p.stop_reason})`;
    case "pipeline_complete":        return "Wrapping up";
    default:                         return ev.type;
  }
}

function MlRunStatusCard({ status, startedAt, recentEvents, summary, failure, onAnalyze }) {
  const theme = useTheme();
  const elapsed = startedAt ? Math.floor((Date.now() - startedAt) / 60000) : 0;

  if (status === "succeeded") {
    // Build a single succinct outcome line: prefer the actual metric from
    // training_done; otherwise just "Model ready". Phase 3 narration in chat
    // takes over from here.
    let summaryLine = "Model ready";
    if (summary) {
      const metric = summary.cv_auc != null
        ? `CV AUC ${summary.cv_auc.toFixed(3)}`
        : (summary.mae != null ? `MAE ${summary.mae.toFixed(3)}` : "");
      const parts = [summary.model_kind, metric].filter(Boolean);
      if (parts.length) summaryLine = `Model ready · ${parts.join(" · ")}`;
    }
    return (
      <Box sx={{
        flexShrink: 0, borderTop: `1px solid ${theme.palette.divider}`,
        px: { xs: 2, sm: 4 }, py: 1,
        bgcolor: `${theme.palette.success.main}0A`,
      }}>
        <Box sx={{ maxWidth: 720, mx: "auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "success.main" }} />
            <Typography variant="caption" fontWeight={600} color="success.main">
              {summaryLine}
            </Typography>
          </Box>
          <Button size="small" variant="contained" color="success" onClick={onAnalyze}
            sx={{ textTransform: "none", borderRadius: 2, fontSize: 12, py: 0.25 }}>
            Analyze results
          </Button>
        </Box>
      </Box>
    );
  }

  if (status === "failed") {
    // "Model training failed" on its own reads as an accusation about the
    // user's data — which is wrong most of the time, since an internal fault
    // is the common case. Lead with whose fault it is; the raw error goes
    // behind a disclosure for anyone who wants it.
    const isOurs = failure?.fault !== "data";
    return (
      <Box sx={{
        flexShrink: 0, borderTop: `1px solid ${theme.palette.divider}`,
        px: { xs: 2, sm: 4 }, py: 1.25,
        bgcolor: `${theme.palette.error.main}0A`,
      }}>
        <Box sx={{ maxWidth: 720, mx: "auto", display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <ErrorOutlineIcon sx={{ fontSize: 16, color: "error.main", mt: "2px" }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" fontWeight={600} color="error.main" sx={{ display: "block" }}>
              {isOurs ? "Model training failed on our side" : "Model training couldn't use this data"}
            </Typography>
            {failure?.message && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                {failure.message}
              </Typography>
            )}
            {failure?.detail && (
              <Box
                component="details"
                sx={{ mt: 0.5, "& summary": { cursor: "pointer", fontSize: 11, color: "text.disabled" } }}
              >
                <summary>Technical detail</summary>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: "block", mt: 0.5, fontFamily: "monospace", fontSize: 11, wordBreak: "break-word" }}
                >
                  {failure.detail}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Render the last 3 events with decreasing opacity. Newest is at
  // index 0 with full opacity; older fade so the eye lands on the freshest
  // milestone without losing context of what just happened.
  const events = (recentEvents || []).map(_eventLine).filter(Boolean);
  return (
    <Box sx={{
      flexShrink: 0, borderTop: `1px solid ${theme.palette.divider}`,
      px: { xs: 2, sm: 4 }, py: 1,
    }}>
      <Box sx={{ maxWidth: 720, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
          <CircularProgress size={11} thickness={5} />
          <Typography variant="caption" color="text.secondary">
            Model training{elapsed > 0 ? ` · ${elapsed} min` : ""}
          </Typography>
        </Box>
        {events.length > 0 && (
          <Box sx={{ mb: 0.75 }}>
            {events.map((line, idx) => (
              <Typography
                key={idx}
                variant="caption"
                color={idx === 0 ? "text.primary" : "text.secondary"}
                sx={{
                  display: "block",
                  fontWeight: idx === 0 ? 500 : 400,
                  opacity: idx === 0 ? 1 : 0.6 - idx * 0.15,
                  fontSize: idx === 0 ? "0.78rem" : "0.72rem",
                  lineHeight: 1.4,
                }}
              >
                {idx === 0 ? "" : "  "}{line}
              </Typography>
            ))}
          </Box>
        )}
        <LinearProgress sx={{ borderRadius: 2, height: 3 }} />
      </Box>
    </Box>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const TERMINAL_ML_STATUSES = ["succeeded", "failed"];
const PHASE3_TRIGGER = "The model finished training. Please analyze the top drivers and present the key business insights.";

/**
 * Props:
 *   sessionId?    — when provided, used instead of useParams().sessionId.
 *                   This lets the component be embedded (e.g. inside the
 *                   Project Report tab) without owning the URL.
 *   projectId?    — same, for the project context.
 *   embedded      — when true, drop the fullscreen styling and the back
 *                   button so the chat sits inside another container.
 */
export default function AnalyzeChat({
  sessionId: sessionIdProp,
  projectId: projectIdProp,
  embedded = false,
  // For embedded callers: a typed message to fire as the first turn once
  // the component mounts. Equivalent to route state.firstUserMessage on
  // the standalone page. The caller passes a callback to know when it's
  // been consumed (so it can clear its local state).
  initialUserMessage = null,
  onInitialMessageConsumed = null,
  // For embedded empty-state callers (e.g. Project Report tab): instead
  // of navigating to a separate URL after first send, hand the created
  // session back to the parent so it can wire project.session_id and
  // re-render in place. When absent, AnalyzeChat navigates (standalone
  // /analyze use).
  onSessionCreated = null,
}) {
  const params = useParams();
  const sessionId = sessionIdProp ?? params.sessionId;
  const urlProjectId = projectIdProp ?? params.projectId;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // When viewing a real user read-only, chatting is a write the backend will
  // reject (403 read_only_view_as) — disable the composer up front instead.
  const { viewAsReadOnly, actingUser } = useImpersonation();

  const [messages, setMessages] = useState([]);
  const [filename, setFilename] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [reportReady, setReportReady] = useState(false);
  const [projectId, setProjectId] = useState(urlProjectId || null);
  const [mlRunId, setMlRunId] = useState(null);
  const [mlRunStatus, setMlRunStatus] = useState(null);
  const [mlRunFailure, setMlRunFailure] = useState(null);
  const [mlRunStartedAt, setMlRunStartedAt] = useState(null);
  // Last 3 progress events (newest at index 0) — rendered as a fading stack
  // in the run card. Captured from the streaming /v2/runs/<id>/events/stream
  // endpoint while the build is in flight.
  const [mlRecentEvents, setMlRecentEvents] = useState([]);
  // Final outcome captured from the training_done event (model_kind, cv_auc).
  // Used to enrich the "succeeded" card with the actual metric.
  const [mlSummary, setMlSummary] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusText, setStatusText] = useState("");

  const messagesEndRef = useRef(null);
  const initialLocationStateRef = useRef(location.state);
  const phase3TriggeredRef = useRef(false);

  // Handoff state: set by the consolidated effect below when we detect
  // an inbound first message. The second effect (further down) fires
  // sendMessage when pendingHandoff is set.
  const [pendingHandoff, setPendingHandoff] = useState(null);

  const isStreaming = messages.length > 0 && messages[messages.length - 1].streaming === true;

  const reportPath = urlProjectId
    ? `/projects/${urlProjectId}/analyze/${sessionId}/report`
    : `/analyze/${sessionId}/report`;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending, reportReady]);

  useEffect(() => {
    // Reset session-derived state on every sessionId change. Without this,
    // navigating from a session that had a failed ML run to one that doesn't
    // leaves the "Model training failed" badge from the previous session,
    // because the ml_run setters below are gated on session.ml_run_id and
    // never explicitly clear. Same story for messages: the previous chat
    // briefly flashes while the next session's GET is in flight.
    setMessages([]);
    setMlRunId(null);
    setMlRunStatus(null);
    setMlRunFailure(null);
    setMlRunStartedAt(null);
    setMlRecentEvents([]);
    setMlSummary(null);
    setReportReady(false);
    phase3TriggeredRef.current = false;

    // No sessionId yet — this is the "empty / landing" state. Skip
    // session-fetch; the inline composer at the bottom will create one
    // on first send.
    if (!sessionId) {
      setLoading(false);
      return;
    }

    // Detect a first-turn handoff from any of: prop, sessionStorage,
    // route state. If we find one, stamp the header + set pendingHandoff
    // and SKIP the backend session fetch — the streaming /chat call
    // (fired by the pendingHandoff effect below) will populate messages.
    // Critical: React Router preserves this instance across the route
    // change from /analyze to /analyze/:sessionId because both routes
    // render the same <Analyze /> element. So we re-derive on every
    // sessionId change rather than relying on useState lazy init.
    //
    // sessionStorage uses a single "analyze_pending" key with a payload
    // {sessionId, message, ts}. Validating sessionId match prevents
    // stale entries from earlier failed handoffs from firing on
    // unrelated sessions (e.g., when the user clicks an old session in
    // the history list). The 30s ts guard adds a second line of
    // defense against any other leak path.
    let handoffMessage = initialUserMessage || null;
    if (!handoffMessage) {
      try {
        const raw = sessionStorage.getItem("analyze_pending");
        if (raw) {
          const parsed = JSON.parse(raw);
          const fresh = parsed && (Date.now() - (parsed.ts || 0)) < 30_000;
          if (parsed && parsed.sessionId === sessionId && fresh) {
            sessionStorage.removeItem("analyze_pending");
            handoffMessage = parsed.message;
          }
        }
      } catch (_) { /* private-mode or malformed; fall through */ }
    }
    const state = initialLocationStateRef.current;
    if (!handoffMessage && state?.firstUserMessage) {
      handoffMessage = state.firstUserMessage;
    }

    if (handoffMessage) {
      if (state) {
        setFilename(state.filename || "");
        setRowCount(state.rowCount || 0);
        setReportReady(state.reportReady || false);
        window.history.replaceState({}, "");
      }
      setLoading(false);
      setPendingHandoff(handoffMessage);
      return;
    }

    if (state?.firstMessage) {
      setMessages([{ role: "assistant", content: state.firstMessage }]);
      setFilename(state.filename || "");
      setRowCount(state.rowCount || 0);
      setReportReady(state.reportReady || false);
      setLoading(false);
      window.history.replaceState({}, "");
      return;
    }

    getAnalyzeSession(sessionId)
      .then((session) => {
        setFilename(session.filename || "");
        setRowCount(session.row_count || 0);
        setReportReady(session.report_ready || false);
        if (!urlProjectId && session.project_id) setProjectId(session.project_id);
        setMessages(session.display_messages || []);
        if (session.ml_run_id) {
          setMlRunId(session.ml_run_id);
          setMlRunStatus(session.ml_run_status || "queued");
          setMlRunFailure(session.ml_run_error ? {
            fault: session.ml_run_fault,
            message: session.ml_run_error,
            detail: session.ml_run_error_detail,
          } : null);
          setMlRunStartedAt(Date.now());
          if (session.ml_run_status === "succeeded") phase3TriggeredRef.current = true;
        }
      })
      .catch((e) => {
        setError(e?.response?.data?.error || e.message || "Failed to load session");
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, initialUserMessage]);

  // Subscribe to v2 build progress via SSE while the run is non-terminal.
  // Replaces the old 20s status poll — events arrive live as the pipeline
  // hits each milestone (intent_classified, label_built, training_done, etc).
  useEffect(() => {
    if (!mlRunId || TERMINAL_ML_STATUSES.includes(mlRunStatus)) return;

    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        await streamV2RunEvents(mlRunId, (ev) => {
          if (cancelled) return;
          // Backend sends two kinds of frames:
          //   - regular event: {seq, type, ts, payload}
          //   - control frames: {type: '_done' | '_synced' | '_timeout' | '_error', ...}
          if (ev?.type === "_done") {
            // v2 status "complete" maps to v1's "succeeded" via the adapter,
            // but the event stream emits raw v2 status. Translate here.
            const final = ev.status === "complete" ? "succeeded" : ev.status || "succeeded";
            setMlRunStatus(final);
            return;
          }
          if (ev?.type && !ev.type.startsWith("_")) {
            // Prepend so newest is at index 0; keep up to 3.
            setMlRecentEvents((prev) => [ev, ...prev].slice(0, 3));
            // Capture training_done specifically for the final card summary.
            if (ev.type === "training_done") {
              const p = ev.payload || {};
              setMlSummary({
                model_kind: p.model_kind,
                cv_auc: p.cv_auc,
                mae: p.mae,
                n_features: p.n_features,
              });
            }
            // mlRunStatus stays "queued"/"running" while events stream in;
            // the _done frame flips it to "succeeded" when the build finishes.
            if (mlRunStatus === "queued") setMlRunStatus("running");
          }
        }, { signal: controller.signal });
      } catch (e) {
        if (!cancelled && e.name !== "AbortError") {
          console.warn("[v2 event stream]", e);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [mlRunId, mlRunStatus]);

  // Safety-net poll for the terminal status. The SSE stream above is the
  // primary (live) source, but a long build can outlive its connection — if the
  // stream drops before the `_done` frame, the handler never learns the run
  // finished and the card spins forever even though the backend has it as
  // succeeded. Poll the session status while non-terminal so a completed/failed
  // run always reconciles within one interval, regardless of the stream.
  useEffect(() => {
    if (!mlRunId || TERMINAL_ML_STATUSES.includes(mlRunStatus)) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await getAnalyzeSession(sessionId);
        if (cancelled) return;
        const st = s?.ml_run_status;
        if (st && TERMINAL_ML_STATUSES.includes(st)) {
          setMlRunStatus(st);
          // The failure payload only exists once the run reaches "failed";
          // the SSE stream carries the status but not the classification.
          setMlRunFailure(s?.ml_run_error ? {
            fault: s.ml_run_fault,
            message: s.ml_run_error,
            detail: s.ml_run_error_detail,
          } : null);
        }
      } catch (_) { /* transient — try again next tick */ }
    };
    const id = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [mlRunId, mlRunStatus, sessionId]);

  // Auto-trigger Phase 3 when model succeeds
  useEffect(() => {
    if (mlRunStatus === "succeeded" && !phase3TriggeredRef.current && !sending) {
      phase3TriggeredRef.current = true;
      sendMessage(PHASE3_TRIGGER);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mlRunStatus]);

  const sendMessage = useCallback(async (msg) => {
    if (!msg || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setSending(true);
    setError("");
    setStatusText("");

    try {
      await sendAnalyzeMessageStreaming(sessionId, msg, (event) => {
        if (event.type === "token") {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === "assistant" && last.streaming) {
              return [...prev.slice(0, -1), { ...last, content: last.content + event.text }];
            }
            return [...prev, { role: "assistant", content: event.text, streaming: true }];
          });
        } else if (event.type === "message_end") {
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.streaming) {
              return [...prev.slice(0, -1), { role: last.role, content: last.content }];
            }
            return prev;
          });
          setStatusText("");
        } else if (event.type === "status") {
          setStatusText(event.text);
        } else if (event.type === "done") {
          if (event.report_ready) setReportReady(true);
        } else if (event.type === "ml_run_created") {
          setMlRunId(event.ml_run_id);
          setMlRunStatus("queued");
          setMlRunStartedAt(Date.now());
        } else if (event.type === "error") {
          setError(event.error || "Something went wrong");
        }
      });
    } catch (e) {
      setError(e.message || "Something went wrong");
    } finally {
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.streaming) {
          return [...prev.slice(0, -1), { role: last.role, content: last.content }];
        }
        return prev;
      });
      setStatusText("");
      setSending(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, sending]);

  // Fire the deferred-first-turn handoff. Runs after sendMessage is
  // defined. Guarded by `pendingHandoff` so it fires at most once even
  // when sendMessage's identity changes (useCallback deps include
  // `sending`, which flips mid-call).
  useEffect(() => {
    if (!pendingHandoff) return;
    const msg = pendingHandoff;
    setPendingHandoff(null);
    sendMessage(msg);
    if (typeof onInitialMessageConsumed === "function") {
      onInitialMessageConsumed();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingHandoff, sendMessage]);

  const handleSend = () => {
    if (viewAsReadOnly) return;
    const msg = input.trim();
    if (msg) sendMessage(msg);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ─── Empty state — no session yet ──────────────────────────────────
  // Same component as the active chat, just with the composer where the
  // input would be and a greeting where the messages would be. After
  // first send, navigation flips sessionId → falls through to the chat
  // render below.
  if (!sessionId) {
    return (
      <AnalyzeEmptyState
        embedded={embedded}
        projectId={urlProjectId}
        error={error}
        navigate={navigate}
        onError={setError}
        onSessionCreated={onSessionCreated}
      />
    );
  }

  const showMlCard = mlRunId && mlRunStatus;

  return (
    <Box sx={{
      display: "flex", flexDirection: "column",
      height: embedded ? "calc(100vh - 280px)" : "calc(100vh - 80px)",
      minHeight: 480,
    }}>
      {/* Header — back button hidden in embedded mode (the parent tab owns nav) */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!embedded && (
            <IconButton size="small" onClick={() => navigate(projectId ? `/projects/${projectId}` : "/analyze")} sx={{ color: "text.secondary" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <AutoGraphIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ maxWidth: 220 }}>
            {filename}
          </Typography>
          {rowCount > 0 && (
            <Chip label={`${rowCount.toLocaleString()} rows`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
          )}
        </Box>

        {reportReady && (
          <Button
            variant="contained" size="small" endIcon={<OpenInNewIcon />}
            onClick={() => navigate(reportPath)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            View Report
          </Button>
        )}
      </Box>

      {/* Report ready banner */}
      {reportReady && (
        <Box sx={{
          bgcolor: `${theme.palette.primary.main}15`,
          borderBottom: `1px solid ${theme.palette.primary.light}`,
          px: 2, py: 1,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Typography variant="body2" color="primary" fontWeight={500}>
            Your analysis report is ready.
          </Typography>
          <Button size="small" variant="contained" onClick={() => navigate(reportPath)}
            sx={{ borderRadius: 2, textTransform: "none" }}>
            Open Report
          </Button>
        </Box>
      )}

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 2, sm: 4 }, py: 2 }}>
        <Box sx={{ maxWidth: 720, mx: "auto" }}>
          {messages.map((msg, i) => (
            <MessageBubble key={i} role={msg.role} content={msg.content} />
          ))}
          {sending && !isStreaming && <TypingIndicator statusText={statusText} />}
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setError("")}>{error}</Alert>
          )}
          <div ref={messagesEndRef} />
        </Box>
      </Box>

      {/* ML run status card */}
      {showMlCard && (
        <MlRunStatusCard
          status={mlRunStatus}
          startedAt={mlRunStartedAt}
          recentEvents={mlRecentEvents}
          summary={mlSummary}
          failure={mlRunFailure}
          onAnalyze={() => {
            // The ref's purpose is to prevent the auto-trigger effect
            // from re-firing, NOT to disable explicit user clicks. The
            // user clicking "Analyze results" should always send the
            // trigger, even if Phase 3 has been auto-fired before
            // (e.g. on a returning session, or the user wants to
            // re-narrate results).
            if (sending) return;
            phase3TriggeredRef.current = true;
            sendMessage(PHASE3_TRIGGER);
          }}
        />
      )}

      {/* Input */}
      <Paper elevation={0} sx={{ flexShrink: 0, borderTop: `1px solid ${theme.palette.divider}`, px: { xs: 2, sm: 4 }, py: 1.5 }}>
        {viewAsReadOnly && (
          <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "text.secondary", mb: 1 }}>
            Read-only — viewing {actingUser?.email || "this user"}. Chatting is disabled.
          </Typography>
        )}
        <Box sx={{ maxWidth: 720, mx: "auto", display: "flex", gap: 1, alignItems: "flex-end" }}>
          <TextField
            fullWidth multiline maxRows={4}
            placeholder={viewAsReadOnly ? "Read-only view — chatting disabled" : "Reply to the assistant…"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending || viewAsReadOnly}
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
          />
          <Tooltip title={viewAsReadOnly ? "Read-only: you're viewing another user's data" : ""} disableHoverListener={!viewAsReadOnly}>
            <span>
              <IconButton
                onClick={handleSend}
                disabled={!input.trim() || sending || viewAsReadOnly}
                sx={{
                  bgcolor: theme.palette.primary.main, color: "white",
                  "&:hover": { bgcolor: theme.palette.primary.dark },
                  "&.Mui-disabled": { bgcolor: theme.palette.action.disabledBackground },
                  borderRadius: 2, width: 40, height: 40, flexShrink: 0,
                }}
              >
                {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Empty state — used when AnalyzeChat mounts without a sessionId ──────────
// One unified entry point: the chat input (with file / table attach buttons)
// + a small history list of past explores. On first send we create a session
// and navigate to /analyze/<id> with the typed message in route state — the
// outer AnalyzeChat re-renders, picks up the message, and starts streaming.

function AnalyzeEmptyState({ embedded, projectId, error, navigate, onError, onSessionCreated }) {
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    // Skip history list in embedded (project) mode — it'd surface unrelated
    // user-scoped sessions inside the project view.
    if (embedded) return;
    listAnalyzeSessions()
      .then((data) => setSessions(Array.isArray(data) ? data : []))
      .catch(() => setSessions([]));
  }, [embedded]);

  // Default: navigate to the new session's URL. Embedded callers (Project
  // Report tab) pass onSessionCreated so they can swap project state in
  // place instead of navigating away.
  //
  // Stashes the firstUserMessage in sessionStorage under a SINGLE key
  // (carrying {sessionId, message, ts}). AnalyzeChat reads it on mount,
  // checks the sessionId matches and ts is recent, then clears. Single
  // key + sessionId check prevents stale entries from earlier failed
  // handoffs from leaking into unrelated sessions when the user clicks
  // them from history. Bulletproof across the route remount whether or
  // not React preserves the instance.
  const handoff = (result, opts = {}) => {
    if (opts.firstUserMessage && result?.session_id) {
      try {
        sessionStorage.setItem(
          "analyze_pending",
          JSON.stringify({
            sessionId: result.session_id,
            message: opts.firstUserMessage,
            ts: Date.now(),
          }),
        );
      } catch (_) { /* private-mode quota; route state path handles */ }
    }
    if (typeof onSessionCreated === "function") {
      onSessionCreated(result, opts);
      return;
    }
    const path = projectId
      ? `/projects/${projectId}/analyze/${result.session_id}`
      : `/analyze/${result.session_id}`;
    navigate(path, {
      state: {
        firstUserMessage: opts.firstUserMessage,
        filename: result.filename,
        rowCount: result.row_count,
        reportReady: result.report_ready,
      },
    });
  };

  const onSubmitFile = async (file, message) => {
    onError("");
    setSubmitting(true);
    try {
      const result = await createAnalyzeSession(file, projectId || null, message, true);
      handoff(result, {
        firstUserMessage: message
          || `Please profile '${file.name}' and suggest the most valuable questions to ask.`,
      });
    } catch (e) {
      onError(e?.response?.data?.error || e.message || "Upload failed");
      setSubmitting(false);
    }
  };

  const onSubmitTables = async (tables, message) => {
    onError("");
    setSubmitting(true);
    try {
      const result = await createAnalyzeSessionFromTables(tables, projectId || null, message, true);
      const tableList = tables.map((t) => `${t.dataset}.${t.table}`).join(", ");
      handoff(result, {
        firstUserMessage: message
          || `Please profile ${tableList} and suggest the most valuable questions to ask.`,
      });
    } catch (e) {
      onError(e?.response?.data?.error || e.message || "Failed to start analysis");
      setSubmitting(false);
    }
  };

  const onSubmitGoal = async (message) => {
    onError("");
    setSubmitting(true);
    try {
      const result = await createAnalyzeSessionFromGoal(message, projectId || null);
      handoff(result, { firstUserMessage: message });
    } catch (e) {
      onError(e?.response?.data?.error || e.message || "Failed to start session");
      setSubmitting(false);
    }
  };

  const greeting = embedded
    ? "What would you like to analyze for this project?"
    : "What would you like to analyze?";

  return (
    <Box sx={{
      display: "flex", flexDirection: "column",
      px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 },
      maxWidth: 760, mx: "auto", width: "100%",
    }}>
      {/* Greeting + composer — top of the page, not vertically centered. */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, textAlign: "center", mt: 2 }}>
        {greeting}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
        Drop a file, pick tables you've already loaded, or just describe your goal.
      </Typography>
      <AnalyzeEntryComposer
        submitting={submitting}
        error={error}
        onSubmitFile={onSubmitFile}
        onSubmitTables={onSubmitTables}
        onSubmitGoal={onSubmitGoal}
      />

      {!embedded && (
        <Box sx={{ mt: 5, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Previous analyses
          </Typography>
          {sessions.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
              You haven't started any analyses yet. Submit above to begin.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {sessions.slice(0, 10).map((s) => {
                // Title: the user's first question if we have it; otherwise
                // the data-source label as a fallback (legacy sessions).
                const title = s.name && s.name !== s.filename ? s.name : (s.filename || "Untitled");
                // Subtitle: data source + size, so the user remembers WHAT
                // they were asking about. Skipped when the title already
                // shows the filename.
                const subtitleParts = [];
                if (s.filename && s.filename !== title) subtitleParts.push(s.filename);
                if (s.row_count > 0) subtitleParts.push(`${s.row_count.toLocaleString()} rows`);
                const subtitle = subtitleParts.join(" · ");
                return (
                  <Box
                    key={s.session_id}
                    onClick={() => navigate(`/analyze/${s.session_id}`)}
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      px: 1.25, py: 0.75, borderRadius: 1, cursor: "pointer",
                      "&:hover": { bgcolor: theme.palette.action.hover },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {title}
                      </Typography>
                      {subtitle && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                          {subtitle}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2, flexShrink: 0 }}>
                      {s.created_at ? formatDistanceToNow(new Date(s.created_at), { addSuffix: true }) : ""}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
